import { useState, useEffect } from "react";
import { Product, OrderDetails } from "../types/restaurant";

interface UseOrderModificationTrackerProps {
  selectedProducts: Product[];
  isEditing: boolean;
  oldProducts: OrderDetails | undefined;
  discount: number;
  tableNumber: string;
  guess: string;
  orderType: string;
  deliveryFee: number;
}

export const useOrderModificationTracker = ({selectedProducts, isEditing, oldProducts, discount, tableNumber, guess, orderType, deliveryFee,}: UseOrderModificationTrackerProps) => {
  const [isOrderModified, setIsOrderModified] = useState(true);

  const checkForOrderChanges = (currentProducts: Product[], oldProductsArray: any[], currentDiscount: number, oldDiscount: number, currentTableNumber: string, oldTableNumber: string, currentGuess: string, oldGuess: string, currentOrderType: string,
    oldOrderType: string,
    currentDeliveryFee: number,
    oldDeliveryFee: number): boolean => {
    
    // Check table number changes
    if (currentTableNumber !== oldTableNumber) {
      return true;
    }

    // Check guess (number of guests) changes
    if (currentGuess !== oldGuess) {
      return true;
    }

    // Check discount changes
    if (currentDiscount !== oldDiscount) {
      return true;
    }

     if (currentOrderType !== oldOrderType) return true;

    // DeliveryFee
    if (currentDeliveryFee !== oldDeliveryFee) return true;

    // If both products are empty, no changes
    if (currentProducts.length === 0 && (!oldProductsArray || oldProductsArray.length === 0)) {
      return false;
    }

    // If different number of products, definitely changed
    if (currentProducts.length !== oldProductsArray.length) {
      return true;
    }

    // If current is empty but old had products, changed
    if (currentProducts.length === 0 && oldProductsArray.length > 0) {
      return true;
    }

    // Create maps for easier comparison
    const currentMap = new Map<string, { quantity: number; note: string }>();
    const oldMap = new Map<string, { quantity: number; note: string }>();

    // Map current products
    currentProducts.forEach((product) => {
      const productId = product._id?.$oid;
      if (productId) {
        currentMap.set(productId, {
          quantity: product.quantity || 0,
          note: product.notes || "",
        });
      }
    });

    // Map old products
    oldProductsArray.forEach((product) => {
      const productId = product._id?.$oid || product.productId || product._id;
      if (productId) {
        oldMap.set(productId, {
          quantity: product.quantity || 0,
          note: product.note || product.notes || "",
        });
      }
    });

    // Check if all keys are the same
    const currentKeys = Array.from(currentMap.keys()).sort();
    const oldKeys = Array.from(oldMap.keys()).sort();

    if (JSON.stringify(currentKeys) !== JSON.stringify(oldKeys)) {
      return true;
    }

    // Check each product for changes
    for (let i = 0; i < currentKeys.length; i++) {
      const id = currentKeys[i];
      const currentData = currentMap.get(id)!;
      const oldData = oldMap.get(id);

      if (!oldData) {
        return true;
      }

      if (currentData.quantity !== oldData.quantity) {
        return true;
      }

      const currentNote = (currentData.note || "").trim();
      const oldNote = (oldData.note || "").trim();
      
      if (currentNote !== oldNote) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    if (isEditing && oldProducts) {
      const oldDiscount = oldProducts.discount ? oldProducts.discount : 0;
      const oldTableNumber = oldProducts.table_number || "";
      const oldGuess = oldProducts.guess || "";
      
      const hasChanges = checkForOrderChanges(selectedProducts, oldProducts.Products, discount, oldDiscount,tableNumber,oldTableNumber,guess,oldGuess,orderType, oldProducts.OrderType || "", deliveryFee, Number(oldProducts.deliveryFee || 0));
      setIsOrderModified(hasChanges);
    } else if (!isEditing) {
      // For new orders, modified if there are any products
      const modified = selectedProducts.length > 0;
      setIsOrderModified(modified);
    }
  }, [selectedProducts, isEditing, oldProducts, discount, tableNumber, guess, orderType, deliveryFee]);

  return { isOrderModified, setIsOrderModified,};
};